import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Battery, Zap, Gauge, Building2, Car, ArrowRight, 
  CheckCircle2, Clock, MapPin, Shield, Leaf
} from 'lucide-react';

const serviceData = {
  'ac-dc': {
    icon: Battery,
    title: 'Karikim AC/DC',
    subtitle: 'Zgjidhje Fleksibël për Çdo Nevojë',
    description: 'Rrjeti ynë ofron si karikues AC ashtu edhe DC, duke ju dhënë fleksibilitetin të zgjidhni sipas kohës dhe nevojave tuaja.',
    heroColor: 'from-blue-600 to-blue-700',
    features: [
      { title: 'Karikim AC (22 kW)', desc: 'Ideal për karikim gjatë natës ose parkime të gjata. Koha e karikimit: 3-4 orë.', price: '' },
      { title: 'Karikim DC (60-180 kW)', desc: 'Për udhëtime të gjata kur keni nevojë për karikim të shpejtë.', price: '' },
    ],
    benefits: [
      'Përshtatshmëri me të gjitha markat e EV',
      'Disponueshmëri 24/7',
      'Pagesa me aplikacion ose RFID',
      'Njoftime në kohë reale'
    ]
  },
  'fast-charging': {
    icon: Zap,
    title: 'Fast Charging',
    subtitle: 'Karikim i Shpejtë DC',
    description: 'Stacionet tona Fast Charging DC ju lejojnë të karikoni automjetin tuaj në vetëm 25-45 minuta - koha ideale për një kafe ose drekë.',
    heroColor: 'from-indigo-600 to-indigo-700',
    features: [
      { title: '60 kW DC', desc: 'Karikim standard DC. Koha mesatare: 45 minuta për 80%.', price: '' },
      { title: '180 kW DC', desc: 'Karikim i shpejtë. Koha mesatare: 25 minuta për 80%.', price: '' },
    ],
    benefits: [
      'Koha optimale për pushime të shkurtra',
      'Vendndodhje strategjike pranë autostradave',
      'Monitorim në kohë reale nga aplikacioni',
      'Teknologji e avancuar ftohëse'
    ]
  },
  'ultra-fast': {
    icon: Gauge,
    title: 'Ultra-Fast Charging',
    subtitle: 'Teknologjia më e Avancuar',
    description: 'Me karikuesit tanë Ultra-Fast 240 kW, mund të arrini 80% karikim në vetëm 15-20 minuta. E ardhmja e karikimit është këtu.',
    heroColor: 'from-purple-600 to-purple-700',
    features: [
      { title: 'Mbi 240 kW DC', desc: 'Karikimi më i shpejtë i mundshëm. 15-20 minuta për 80%.', price: '' },
      { title: 'CCS & GB/T', desc: 'Mbështetje për të gjitha standardet e karikimit të shpejtë.', price: '' },
    ],
    benefits: [
      'Koha më e shkurtër e karikimit në treg',
      'Ideale për udhëtime të gjata',
      'Teknologji ftohëse e avancuar',
      'Mbështetje për bateri të gjeneratës së re'
    ]
  },
  'business': {
    icon: Building2,
    title: 'Zgjidhje për Biznese',
    subtitle: 'Infrastrukturë Karikimi për Kompaninë Tuaj',
    description: 'Instaloni stacione karikimi në ambientet e punës dhe ofroni karikimin si benefit për punonjësit ose klientët tuaj.',
    heroColor: 'from-emerald-600 to-emerald-700',
    features: [
      { title: 'Instalim i Plotë', desc: 'Nga planifikimi deri tek instalimi - ne kujdesemi për gjithçka.', price: 'Ofertë' },
      { title: 'Dashboard Menaxhimi', desc: 'Kontrolloni akseset, monitoroni konsumin dhe menaxhoni faturimin.', price: 'Përfshirë' },
    ],
    benefits: [
      'Rritni atraktivitetin si punëdhënës',
      'Tërhiqni klientë me EV',
      'Kontribuoni në qëndrueshmëri',
      'Faturim i thjeshtë dhe transparent'
    ]
  },
  'fleet': {
    icon: Car,
    title: 'Menaxhim Flotash',
    subtitle: 'Zgjidhje të Plota për Flota Elektrike',
    description: 'Platforma jonë e dedikuar për menaxhimin e flotave elektrike. Monitorim në kohë reale, optimizim kostosh dhe raporte të detajuara.',
    heroColor: 'from-orange-600 to-orange-700',
    features: [
      { title: 'Dashboard Qendror', desc: 'Monitoroni të gjithë flotën nga një platformë e vetme.', price: 'Përfshirë' },
      { title: 'Raporte Automatike', desc: 'Fatura dhe raporte konsumi çdo muaj automatikisht.', price: 'Përfshirë' },
    ],
    benefits: [
      'Ulje e kostove operative deri 30%',
      'Planifikim i karikimeve',
      'Alokimi i buxhetit për automjet',
      'Integrim me sistemet tuaja'
    ]
  }
};

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = serviceData[serviceId];
  
  if (!service) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Shërbimi nuk u gjet</h1>
          <Link to="/services" className="text-blue-600 hover:underline">
            Kthehu tek Shërbimet
          </Link>
        </div>
      </div>
    );
  }
  
  const IconComponent = service.icon;
  
  return (
    <div data-testid={`service-${serviceId}-page`} className="min-h-screen pt-20">
      {/* Hero */}
      <section className={`section-padding bg-gradient-to-br ${service.heroColor} text-white relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <IconComponent className="w-5 h-5" />
              <span className="text-sm font-medium">Shërbimet</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
              {service.title}
            </h1>
            <p className="text-2xl text-white/80 mb-6">{service.subtitle}</p>
            <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-2xl">
              {service.description}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-bold"
              >
                <Link to="/stations">
                  Gjej Stacion
                  <MapPin className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-bold"
              >
                <Link to="/contact">
                  Na Kontaktoni
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {service.features.map((feature, index) => (
              <div 
                key={index}
                className="bg-slate-50 rounded-3xl p-8"
              >
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-500 mb-4">{feature.desc}</p>
                {feature.price && (
                  <p className="text-3xl font-bold text-blue-600">{feature.price}</p>
                )}
              </div>
            ))}
          </div>
          
          {/* Benefits */}
          <div className="bg-blue-50 rounded-3xl p-8 md:p-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Përfitimet</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {service.benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  <span className="text-slate-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Keni Pyetje?
          </h2>
          <p className="text-lg text-slate-500 mb-8 max-w-xl mx-auto">
            Ekipi ynë është i gatshëm t'ju ndihmojë. Kontaktoni për më shumë informacion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 text-lg font-bold"
            >
              <Link to="/contact">
                Na Kontaktoni
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-bold"
            >
              <Link to="/services">
                Shiko të Gjitha Shërbimet
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
